<template>
  <div class="w-100">
    <d-alert
      v-if="!loaded && !loadError"
      show
      theme="light"
      class="text-center">
      {{ $t('ui.dataLoading') }}&hellip;
    </d-alert>

    <d-alert
      v-if="loadError"
      show
      theme="warning"
      class="text-center">
      {{ $t('ui.dataLoadingFailed') }}&hellip;
    </d-alert>

    <template v-if="loaded && !loadError">
      <d-alert
        v-if="!editions.length"
        show
        theme="warning"
        class="text-center">
        {{ $t(translationsKey + '.noRecords') }}
      </d-alert>

      <table
        v-else
        class="table mt-0 mb-0 full-width">
        <thead class="bg-light">
          <tr>
            <th scope="col" class="border-0">{{ $t('ui.editionsHistory.date') }}</th>
            <th scope="col" class="border-0">{{ $t('ui.editionsHistory.action') }}</th>
            <th scope="col" class="border-0">{{ $t('ui.editionsHistory.modifiedElement') }}</th>
            <th scope="col" class="border-0">{{ $t('ui.editionsHistory.user') }}</th>
            <th scope="col" class="border-0">{{ $t('ui.editionsHistory.actions') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(row, index) of editions"
            :key="'edition-history-row-' + index">
            <td class="align-middle">
              {{ formatDate(row.created_at) }}
            </td>
            <td class="align-middle">
              <d-badge :theme="getActionTheme(row.action)">
                {{ getActionLabel(row.action) }}
              </d-badge>
            </td>
            <td class="align-middle">
              {{ getSourceLabel(row.source_type) }}
            </td>
            <td class="align-middle">
              {{ row.user_name }}
            </td>
            <td class="align-middle">
              <d-button
                v-if="row.action === 'UPDATE'"
                theme="warning"
                @click.prevent="openDiffPopup(row.id)">
                {{ $t('ui.editionsHistory.seeChanges') }}
              </d-button>
            </td>
          </tr>
        </tbody>
      </table>

      <pagination
        v-if="editions.length"
        :meta-data="paginationMetaData"
        :initial-page="currentPage" />
    </template>

    <editions-history-diff-popup
      v-if="showDiffPopup"
      :endpoint-base="endpointBase"
      :history-item-id="diffItemId"
      @close="closeDiffPopup" />
  </div>
</template>

<script>
import debounce from 'lodash.debounce';
import EditionsHistoryDiffPopup from './EditionsHistoryDiffPopup.vue';
import HasPagination from './../../mixins/HasPagination.vue';
import ListUtils from './../../utils/ListUtils.js';
import Pagination from './Pagination.vue';

export default {
  name: 'editions-history',
  mixins: [
    HasPagination
  ],
  props: {
    endpointBase: {
      type: String,
      required: true
    },
    whereKey: {
      type: String,
      required: true
    },
    entityId: {
      type: [Number, String],
      required: true
    },
    subviewName: {
      type: String,
      required: true
    },
    translationsKey: {
      type: String,
      required: true
    }
  },
  components: {
    'editions-history-diff-popup': EditionsHistoryDiffPopup,
    'pagination': Pagination
  },
  data () {
    return {
      editions: [],
      loaded: false,
      loadError: false,
      showDiffPopup: false,
      diffItemId: false,
      skipLSPagination: true
    };
  },
  watch: {
    entityId (newValue) {
      if (parseInt(newValue, 10) > 0) {
        this.currentPage = 1;
        this.debouncedLoadFilteredData();
      }
    }
  },
  mounted () {
    this.$bus.$on('editions-history-reload', this.reload);

    if (parseInt(this.entityId, 10) > 0) {
      this.debouncedLoadFilteredData();
    }
  },
  methods: {
    reload () {
      this.$bus.$emit('pagination-reset');
    },
    debouncedLoadFilteredData: debounce(function () {
      this.loadFilteredData();
    }, 250),
    loadFilteredData () {
      ListUtils.loadPaginatedItemsData(this, {
        endpoint: this.endpointBase + '/items?where[' + this.whereKey + ']=' + parseInt(this.entityId, 10) + '&page=' + this.currentPage,
        method: 'get',
        listField: 'editions'
      });
    },
    formatDate (date) {
      if (!date) {
        return '';
      }

      return date.replace('T', ' ').replace('.000000Z', '');
    },
    getActionTheme (action) {
      if (action === 'ADD') {
        return 'success';
      }

      if (action === 'UPDATE') {
        return 'warning';
      }

      if (action === 'DELETE') {
        return 'danger';
      }

      return 'gray';
    },
    getActionLabel (action) {
      let key = 'ui.editionsHistory.actionsList.' + action;

      return this.$te(key) ? this.$t(key) : action;
    },
    getSourceLabel (sourceType) {
      let key = this.translationsKey + '.sources.' + sourceType;

      return this.$te(key) ? this.$t(key) : sourceType;
    },
    openDiffPopup (idToLoad) {
      this.showDiffPopup = true;
      this.diffItemId = idToLoad;
    },
    closeDiffPopup () {
      this.showDiffPopup = false;
      this.diffItemId = false;
    }
  },
  beforeDestroy () {
    this.$bus.$off('editions-history-reload', this.reload);
  }
}
</script>
